import { useContext, useState } from "react";
import * as FaIcons from "react-icons/fa";
import LeloTable from "./LeloTable";
import LeloCards from "./LeloCards";
import AdminLeloContext from "../../context/adminLelo/adminLeloContext";
import FormToggles from "./FormToggles";
import LeloColumnManager from "./LeloColumnManager";
import LeloFilterSidebar from "./LeloFilterSidebar";
import { FaSpinner } from "react-icons/fa";

const LeloFormList = ({ items }) => {
  const adminLeloContext = useContext(AdminLeloContext);
  const {
    LeloListStatus,
    filteredEntries,
    currentPage,
    pageSize,
    totalCount,
    loading,
    searchText,
    setSession,
    setCurrentPage,
    appliedFilters,
    getLeloEntries,
    startDate,
    endDate,
    listView,
  } = adminLeloContext;

  const { inactive, active, signed } = LeloListStatus;

  // Sidebar states
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);

  // Track selected session from filters for pre-registration
  const [selectedSession, setSelectedSession] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  // Use the correct data source - prioritize items prop, then filteredEntries
  const itemContent = items || filteredEntries || [];

  const totalPages = Math.ceil(totalCount / pageSize);
  const startRecord = ((currentPage - 1) * pageSize) + 1;
  const endRecord = Math.min(currentPage * pageSize, totalCount);

  // Fixed helper function to get current status - prioritize appliedFilters
  const getCurrentStatus = () => {
    // First check appliedFilters (most reliable source)
    if (appliedFilters && appliedFilters.status) {
      return appliedFilters.status;
    }

    // Fallback to LeloListStatus boolean checks
    if (active && !inactive && !signed) return "Active";
    if (!active && inactive && !signed) return "Inactive";
    if (!active && !inactive && signed) return "Signed";

    // Default fallback
    return "Active";
  };

  // Get all current filters for pagination
  const getCurrentFilters = () => {
    const currentStatus = getCurrentStatus();
    const baseFilters = {
      status: currentStatus,
      pageSize,
    };

    // Add additional filters from appliedFilters if they exist
    if (appliedFilters) {
      if (appliedFilters.permaLink) baseFilters.permaLink = appliedFilters.permaLink;
      if (appliedFilters.startDate) baseFilters.startDate = appliedFilters.startDate;
      if (appliedFilters.endDate) baseFilters.endDate = appliedFilters.endDate;
    }
    if (setSession) {
      baseFilters.session = setSession;
    }
    // Add search text if exists
    if (searchText && searchText.trim() !== '') {
      baseFilters.searchText = searchText;
    }

    return baseFilters;
  };

  const handleNextPage = async () => {
    if (currentPage < totalPages && !loading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);

      try {
        const filters = getCurrentFilters();
        await getLeloEntries({
          ...filters,
          permaLink: setSession,
          searchText,
          page: nextPage,
          startDate,
          endDate,
        });
      } catch (error) {
        console.error('Error fetching next page:', error);
        setCurrentPage(currentPage);
      }
    }
  };

  const handlePreviousPage = async () => {
    if (currentPage > 1 && !loading) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);

      try {
        const filters = getCurrentFilters();
        await getLeloEntries({
          ...filters,
          permaLink: setSession,
          page: prevPage,
          searchText,
          startDate,
          endDate,
        });
      } catch (error) {
        console.error('Error fetching previous page:', error);
        setCurrentPage(currentPage);
      }
    }
  };

  // Show pagination if we have more records than pageSize and not loading
  const showPagination = totalCount > pageSize && !loading && itemContent.length > 0;

  return (
    <div className="forms-list">
      {/* Header Section */}
      <div className="list-header">
        <div className="list-header-left">
          <h2 className="list-title">Secondary Enrollments</h2>
          <p className="list-subtitle">Manage and review Secondary Enrollment submissions</p>
        </div>
      </div>

      {/* Form Toggles / Toolbar */}
      <FormToggles
        onOpenFilterSidebar={() => setIsFilterSidebarOpen(true)}
        onOpenColumnManager={() => setIsColumnManagerOpen(true)}
        selectedSession={selectedSession}
        selectedSessionId={selectedSessionId}
      />

      {/* Record Count */}
      {loading ? (
        <div className="record-count">
          <p>Loading...</p>
        </div>
      ) : (
        <p className="record-count">
          {totalCount > 0
            ? `${startRecord}-${endRecord} of ${totalCount} Records`
            : '0 Records'
          }
        </p>
      )}

      {loading ? (
        <div className="interest-complete">
          <FaSpinner className="spinner-icon" />
          <p>Loading Please wait...</p>
        </div>
      ) : (
        <>
          {itemContent.length > 0 ? (
            listView ? (
              <LeloTable items={itemContent} />
            ) : (
              <LeloCards items={itemContent} />
            )
          ) : (
            <div className="interest-complete">
              <FaIcons.FaCheckSquare className="done-icon" />
              <p>
                {searchText
                  ? 'No records found matching your search criteria'
                  : "Looks like we're out of Secondary Enrollments to process"
                }
              </p>
              {!searchText && <p>Good Job!</p>}
            </div>
          )}

          {/* Pagination Buttons */}
          {showPagination && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={handlePreviousPage}
                disabled={currentPage === 1 || loading}
              >
                <FaIcons.FaChevronLeft />
              </button>

              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>

              <button
                className="pagination-btn"
                onClick={handleNextPage}
                disabled={currentPage === totalPages || loading}
              >
                <FaIcons.FaChevronRight />
              </button>
            </div>
          )}
        </>
      )}

      {/* Filter Sidebar - Always rendered to maintain state */}
      <LeloFilterSidebar
        isOpen={isFilterSidebarOpen}
        onClose={() => setIsFilterSidebarOpen(false)}
        onSessionSelect={(session, sessionId) => {
          setSelectedSession(session);
          setSelectedSessionId(sessionId);
        }}
      />

      {/* Column Manager Sidebar - Always rendered to maintain state */}
      <LeloColumnManager
        isOpen={isColumnManagerOpen}
        onClose={() => setIsColumnManagerOpen(false)}
      />
    </div>
  );
};

export default LeloFormList;
